import { useId } from 'react';
import { Box, Typography } from '@mui/material';

import {
  FEEDBACK_ACCENT,
  FEEDBACK_FIELD_LABEL,
  FEEDBACK_FIELD_PLACEHOLDER,
  FEEDBACK_FIELD_SHELL,
  FEEDBACK_FIELD_TEXT,
} from './constants';

const shellSx = {
  backgroundColor: FEEDBACK_FIELD_SHELL,
  boxShadow: '0px -1px 0px 0px #3C3F40 inset, 0px 1px 0px 0px #3C3F41 inset',
  p: 2,
};

const controlSx = {
  width: '100%',
  background: 'transparent',
  color: FEEDBACK_FIELD_TEXT,
  WebkitTextFillColor: FEEDBACK_FIELD_TEXT,
  caretColor: FEEDBACK_FIELD_TEXT,
  border: 0,
  outline: 'none',
  fontSize: 16,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  p: 0,
  boxSizing: 'border-box',
  resize: 'vertical',
  '&::placeholder': {
    color: FEEDBACK_FIELD_PLACEHOLDER,
    opacity: 1,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

/**
 * Feedback form control using the app's native dark FormInput chrome.
 */
const FeedbackField = ({
  label,
  helperText,
  multiline = false,
  minRows = 6,
  maxLength,
  value = '',
  id: idProp,
  sx,
  ...rest
}) => {
  const generatedId = useId();
  const fieldId = idProp || generatedId;
  const helperId = helperText ? `${fieldId}-helper` : undefined;
  const counterId = maxLength != null ? `${fieldId}-counter` : undefined;
  const describedBy = [helperId, counterId].filter(Boolean).join(' ') || undefined;
  const length = typeof value === 'string' ? value.length : 0;

  const sharedProps = {
    id: fieldId,
    value,
    maxLength,
    'aria-describedby': describedBy,
    ...rest,
  };

  return (
    <Box sx={sx}>
      <Box
        sx={{
          ...shellSx,
          outline: 'none',
          transition: 'box-shadow 120ms ease',
          '&:focus-within': {
            boxShadow: `inset 0 0 0 2px ${FEEDBACK_ACCENT}`,
          },
        }}
      >
        {label && (
          <Typography
            component="label"
            htmlFor={fieldId}
            sx={{ color: FEEDBACK_FIELD_LABEL, fontSize: 12, fontWeight: 700, mb: 0.75, display: 'block' }}
          >
            {label}
          </Typography>
        )}
        {multiline ? (
          <Box
            component="textarea"
            {...sharedProps}
            sx={{
              ...controlSx,
              minHeight: minRows * 24,
            }}
          />
        ) : (
          <Box
            component="input"
            {...sharedProps}
            sx={{
              ...controlSx,
              height: 24,
              resize: 'none',
            }}
          />
        )}
      </Box>
      {(helperText || maxLength != null) && (
        <Box display="flex" justifyContent="space-between" gap={2} mt={1}>
          {helperText ? (
            <Typography id={helperId} fontSize={13} sx={{ opacity: 0.75, lineHeight: 1.45 }}>
              {helperText}
            </Typography>
          ) : (
            <span />
          )}
          {maxLength != null && (
            <Typography
              id={counterId}
              fontSize={12}
              sx={{ opacity: 0.55, flexShrink: 0 }}
              aria-live="polite"
            >
              {length}/{maxLength}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FeedbackField;
